import {
  AutoAwesome,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { groupBy } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import { updateHorseSuggestion } from '../../../../../redux/horse/actions';

const QuestriSuggestionsDialog = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();

  const horseSuggestion = useSelector((state) => state.horse.horseSuggestion);
  const isFetchingHorseSuggestion = useSelector((state) => state.horse.isFetchingHorseSuggestion);
  const eventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);

  const groupedTasks = React.useMemo(() => (horseSuggestion
    ? groupBy(horseSuggestion.recentTaskRecords, 'taskTypeId')
    : {}), [horseSuggestion]);

  const templateLookup = React.useMemo(() => groupBy(eventTemplates || [], 'id'), [eventTemplates]);

  const hiddenSuggestions = horseSuggestion?.hiddenTaskSuggestions || [];
  const suggestionsEnabled = horseSuggestion && !horseSuggestion.hideAllSuggestions;

  const handleToggleAll = () => {
    if (!horseSuggestion || isFetchingHorseSuggestion) return;
    dispatch(updateHorseSuggestion(
      horseSuggestion.id,
      { hideAllSuggestions: !horseSuggestion.hideAllSuggestions },
    ));
  };

  const handleToggleTask = (taskTypeId, taskIsHidden) => {
    if (!horseSuggestion || isFetchingHorseSuggestion) return;
    dispatch(updateHorseSuggestion(
      horseSuggestion.id,
      {
        hiddenTaskSuggestions: taskIsHidden
          ? hiddenSuggestions.filter((t) => t !== taskTypeId)
          : [...hiddenSuggestions, taskTypeId],
      },
    ));
  };

  const renderToggleIcon = (taskIsHidden) => {
    if (isFetchingHorseSuggestion) {
      return <CircularProgress size={16} />;
    }
    if (taskIsHidden) {
      return <Visibility />;
    }
    return <VisibilityOff />;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      scroll="paper"
      aria-labelledby="questri-suggestions-title"
    >
      <DialogTitle
        id="questri-suggestions-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pr: {
            xs: 3,
            sm: 4,
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '30%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'secondary.light',
            color: '#FF6600',
          }}
        >
          <AutoAwesome fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
            Questri Suggestions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Recently planned tasks help Questri recommend what to schedule next.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 3 }}>
        {horseSuggestion
          ? (
            <Stack spacing={3}>
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                spacing={2}
                alignItems={{
                  xs: 'flex-start',
                  sm: 'center',
                }}
                justifyContent="space-between"
              >
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={suggestionsEnabled}
                      onChange={handleToggleAll}
                      disabled={isFetchingHorseSuggestion}
                    />
                  )}
                  labelPlacement="start"
                  label="Show Suggestions"
                  sx={{ m: 0 }}
                />
                <Chip
                  color="secondary"
                  variant="outlined"
                  size="small"
                  label={`${horseSuggestion.recentTaskRecords?.length || 0} recent task${(horseSuggestion.recentTaskRecords?.length || 0) > 1
                    ? 's'
                    : ''}`}
                />
              </Stack>
              {!suggestionsEnabled && (
                <Alert severity="info" variant="outlined">
                  Suggestions are hidden for this horse. Enable them to see planning recommendations on
                  the barn board and within this dialog.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                Questri recommends tasks that have recently been planned for this horse. Hide a task if you
                no longer want it to be suggested.
              </Typography>
              {suggestionsEnabled && (
                <List
                  dense
                  disablePadding
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                  subheader={(
                    <ListSubheader
                      component="div"
                      disableSticky
                      sx={{
                        bgcolor: 'transparent',
                        pb: 1,
                      }}
                    >
                      <Typography color="text.secondary" variant="body2">
                        Recently Planned Tasks (last 30 days)
                      </Typography>
                    </ListSubheader>
                  )}
                >
                  {Object.keys(groupedTasks)
                    .sort((a, b) => groupedTasks[b].length - groupedTasks[a].length)
                    .map((taskId) => {
                      const parsedId = parseInt(taskId, 10);
                      const template = templateLookup[parsedId]?.[0];
                      const taskIsHidden = hiddenSuggestions.includes(parsedId);
                      return (
                        <ListItem
                          key={`suggestion-${parsedId}-${taskIsHidden}`}
                          secondaryAction={(
                            <Button
                              size="small"
                              color={taskIsHidden
                                ? 'primary'
                                : 'secondary'}
                              endIcon={renderToggleIcon(taskIsHidden)}
                              onClick={() => handleToggleTask(parsedId, taskIsHidden)}
                            >
                              {taskIsHidden
                                ? 'Show Suggestions'
                                : 'Hide Task'}
                            </Button>
                          )}
                        >
                          <ListItemText
                            primary={template?.name || 'Unknown Task'}
                            secondary={`Used ${groupedTasks[taskId].length} time${groupedTasks[taskId].length > 1
                              ? 's'
                              : ''}`}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              )}
            </Stack>
          )
          : (
            <Stack spacing={2}>
              <Typography variant="body1" fontWeight={600}>
                Start planning to unlock suggestions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plan at least two weeks of tasks for this horse and Questri will surface helpful templates
                you can add in a couple of taps.
              </Typography>
            </Stack>
          )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
        }}
      >
        <Button color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

QuestriSuggestionsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default QuestriSuggestionsDialog;
